import React from "react";
import { Box, FormControl, IconButton, Typography } from "@material-ui/core";
import ClearIcon from "@material-ui/icons/Clear";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import { useSelector } from "react-redux";
import { Controller, useFormContext } from "react-hook-form";
import { ActionTextField } from "pages/CreateSchedulerRule/components";
import { useSelectOptions } from "hooks";
import { getEmailSchedulerDefinitions } from "store/features/emailScheduler/definition";
import { ActionViewModel } from "validation";
import { Select } from "components";
import { ArrayField } from "react-hook-form/dist/types";

interface SchedulerActionProps {
    index: number;
    item: Partial<ArrayField<ActionViewModel>>;
    title: string;
    isDeleteDisabled: boolean;
    onRemoveAction(index?: number | number[] | undefined): void;
    onValidationToggle?(payload?: string | string[] | undefined): Promise<boolean>;
}

/**
 * @param index Row number
 * @param isDeleteDisabled Disable row removal
 * @param title Action title
 * @param item Action field
 * @param onRemoveAction Field delete handler
 * @constructor
 */
export const Action = ({ index, item, isDeleteDisabled, title, onRemoveAction }: SchedulerActionProps) => {
    const { control, errors, trigger } = useFormContext();
    const { templates, statuses } = useSelector(getEmailSchedulerDefinitions);
    const templateDetails = useSelectOptions(templates);
    const statusDetails = useSelectOptions(statuses);

    return (
        <Box display="flex" mt={1} mb={1} mr={2} alignItems="center">
            <Box flex={0.9} border={1} borderColor="grey.400">
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    pl={2}
                    pr={2}
                    bgcolor="secondary.50">
                    <Box>
                        <Typography variant="subtitle2">{title}</Typography>
                    </Box>
                    <Box justifyContent="flex-end">
                        <IconButton onClick={() => console.log("CLICKED")}>
                            <FileCopyIcon color="primary" fontSize="small" />
                        </IconButton>
                        <IconButton onClick={() => onRemoveAction(index)} disabled={isDeleteDisabled}>
                            <ClearIcon color={isDeleteDisabled ? "disabled" : "error"} fontSize="default" />
                        </IconButton>
                    </Box>
                </Box>

                <Box display="flex" alignItems="flex-start" p={2}>
                    <Box display="flex" flex={0.5} justifyContent="space-between" mr={4}>
                        <FormControl>
                            <Controller
                                name={`actions[${index}].startsAt.days`}
                                control={control}
                                defaultValue={item.startsAt?.days}
                                as={
                                    <ActionTextField
                                        type="number"
                                        label="Days"
                                        error={!!errors.actions?.[index]?.startsAt?.days}
                                        helperText={errors.actions?.[index]?.startsAt?.days?.message}
                                        inputProps={{
                                            steps: 1,
                                        }}
                                    />
                                }
                            />
                        </FormControl>
                        <FormControl>
                            <Controller
                                name={`actions[${index}].startsAt.hours`}
                                control={control}
                                defaultValue={item.startsAt?.hours}
                                as={
                                    <ActionTextField
                                        type="number"
                                        label="Hours"
                                        error={!!errors.actions?.[index]?.startsAt?.hours}
                                        helperText={errors.actions?.[index]?.startsAt?.hours?.message}
                                        inputProps={{
                                            steps: 1,
                                        }}
                                    />
                                }
                            />
                        </FormControl>
                        <FormControl>
                            <Controller
                                name={`actions[${index}].startsAt.minutes`}
                                control={control}
                                defaultValue={item.startsAt?.minutes}
                                as={
                                    <ActionTextField
                                        type="number"
                                        error={!!errors.actions?.[index]?.startsAt?.minutes}
                                        helperText={errors.actions?.[index]?.startsAt?.minutes?.message}
                                        label="Min."
                                        inputProps={{
                                            steps: 1,
                                        }}
                                    />
                                }
                            />
                        </FormControl>
                    </Box>

                    <Select
                        defaultValue={item.template}
                        name={`actions[${index}].template`}
                        title="Template"
                        hasEmptyOption={true}
                        error={errors.actions?.[index]?.template}
                        options={templateDetails}
                        onChange={(onChange, value) => {
                            // TODO: temporary re-validate when template is picked
                            setTimeout(() => {
                                trigger?.([`actions[${index}].status`, `actions[${index}].template`]);
                            });

                            onChange(value);
                        }}
                    />
                    <Select
                        defaultValue={item.status}
                        name={`actions[${index}].status`}
                        title="Set status"
                        error={errors.actions?.[index]?.status}
                        options={statusDetails}
                    />
                </Box>
            </Box>
        </Box>
    );
};
